import { HStack, forwardRef, transition } from '@hover/blueprint';
import React from 'react';

import {
  isOriginalImageDisplay,
  withHover,
} from 'src/modules/homeownerApp/helpers';
import { useGeneratedImageLabel } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';

import { CarouselBadge, getCarouselThumbnailStyles } from '../CarouselBadge';
import { CustomizeButton } from '../CustomizeButton';
import { SaveButton } from '../SaveButton';
import { ShareButton } from '../ShareButton';

export interface ThumbnailEngagementButtonsProps {
  image: AiDisplayImageFragment;
  alwaysShow?: boolean;
}

export const ThumbnailEngagementButtons = forwardRef<
  ThumbnailEngagementButtonsProps,
  'div'
>(({ image, alwaysShow }, ref) => {
  const imageLabel = useGeneratedImageLabel(image.aiGeneratedImage);
  const styles = getCarouselThumbnailStyles(alwaysShow);

  return (
    <>
      <HStack
        data-testid="thumbnail-overlay-heading"
        display="flex"
        left={400}
        pointerEvents="none"
        position="absolute"
        right={400}
        top={400}
      >
        <CarouselBadge label={imageLabel} sx={styles} />
      </HStack>

      <HStack
        bottom={400}
        data-testid="thumbnail-overlay-buttons"
        display={alwaysShow ? 'flex' : 'none'}
        justifyContent="space-between"
        left={400}
        pointerEvents="none"
        position="absolute"
        ref={ref}
        right={400}
        sx={{ ...withHover({ display: 'flex' }) }}
      >
        <CustomizeButton
          {...transition.easing250('opacity')}
          _focus={{ opacity: 1 }}
          colorScheme="light"
          image={image}
          pointerEvents="auto"
          sx={styles}
          variant="glass"
        />
        {!isOriginalImageDisplay(image) && (
          <HStack
            _focusWithin={{ opacity: 1 }}
            cursor="pointer"
            justifyContent="end"
            opacity={1}
            pointerEvents="auto"
            spacing={200}
          >
            <ShareButton
              aiDisplayImage={image}
              {...transition.easing250('opacity')}
              colorScheme="light"
              sx={styles}
              variant="glass"
            />
            <SaveButton
              {...transition.easing250('opacity')}
              colorScheme="light"
              image={image}
              sx={styles}
              variant="glass"
            />
          </HStack>
        )}
      </HStack>
    </>
  );
});
